import { createFileRoute } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";

import { SiteFooter, SiteHeader } from "@/components/layout/SiteChrome";

const BASE_URL = "https://adkiro.com";
const PAGE_URL = `${BASE_URL}/guides`;
const TITLE = "Meta Ads Guides | Adkiro";
const DESCRIPTION =
  "Practical explainers on Meta Ads for property: Dubai's advertising permit, Meta's housing rules, delivery, account structure and creative testing. Written to answer the question, not to sell.";

const GUIDES = [
  {
    href: "/guides/dubai-property-ad-permit",
    eyebrow: "Dubai property",
    title: "The Dubai property ad permit and its QR code, on Meta",
    blurb:
      "A property advertisement in Dubai needs a Land Department permit and, since April 2023, its QR code. What that changes in the order a Facebook or Instagram campaign is built.",
    minutes: 6,
  },
  {
    href: "/guides/meta-housing-rules-dubai-property",
    eyebrow: "Dubai property",
    title: "Meta's housing rules, when Dubai property is advertised to buyers abroad",
    blurb:
      "Property ads shown in the US, Canada or Europe must be declared as housing ads, which removes age, gender and postcode targeting, wherever the advertiser is based.",
    minutes: 6,
  },
  {
    href: "/guides/why-meta-ad-costs-rise",
    eyebrow: "Diagnostics",
    title: "Why Meta ad costs rise while conversions fall",
    blurb:
      "Cost per acquisition is an outcome of CPM, click-through rate and conversion rate. Identifying which one moved is what separates a fix from a rebuild.",
    minutes: 7,
  },
  {
    href: "/guides/meta-ads-learning-phase",
    eyebrow: "Delivery",
    title: "How the Meta Ads learning phase works, and how to leave it",
    blurb:
      "The roughly 50 weekly events an ad set needs applies per ad set, not per account. That single fact explains most chronically unstable delivery.",
    minutes: 7,
  },
  {
    href: "/guides/meta-ads-campaign-structure",
    eyebrow: "Account structure",
    title: "How to structure Meta Ads campaigns",
    blurb:
      "How many ad sets an account can actually support, why overlapping audiences raise costs, and when a separate campaign is genuinely justified.",
    minutes: 8,
  },
  {
    href: "/guides/meta-advantage-plus",
    eyebrow: "Automation",
    title: "When Meta Advantage+ helps, and when it does not",
    blurb:
      "Advantage+ is several separate automations, not one switch. Which to adopt, which creative enhancements to switch off, and why it still needs conversion volume.",
    minutes: 8,
  },
  {
    href: "/guides/meta-ads-creative-testing",
    eyebrow: "Creative",
    title: "How to test Meta ad creative without wasting budget",
    blurb:
      "Why most tests return no usable answer, where the differences actually come from, and how to tell fatigue apart from a concept that never worked.",
    minutes: 7,
  },
];

export const Route = createFileRoute("/guides_")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:url", content: PAGE_URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: `${BASE_URL}/logo.png` },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@graph": [
            {
              "@type": "CollectionPage",
              "@id": `${PAGE_URL}#collection`,
              name: "Meta Ads Guides",
              description: DESCRIPTION,
              url: PAGE_URL,
              isPartOf: { "@id": `${BASE_URL}/#website` },
              publisher: { "@id": `${BASE_URL}/#organization` },
            },
            {
              "@type": "ItemList",
              itemListElement: GUIDES.map((guide, index) => ({
                "@type": "ListItem",
                position: index + 1,
                name: guide.title,
                url: `${BASE_URL}${guide.href}`,
              })),
            },
            {
              "@type": "BreadcrumbList",
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: `${BASE_URL}/` },
                { "@type": "ListItem", position: 2, name: "Guides", item: PAGE_URL },
              ],
            },
          ],
        }),
      },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader tone="ink" />

      <main>
        <section data-tone="ink" className="tone-ink">
          <div className="mx-auto max-w-4xl px-5 pb-12 pt-14 lg:px-8 lg:pt-20">
            <div className="hero-eyebrow">
              <span className="hero-eyebrow__line" />
              <span>Guides</span>
            </div>
            <h1 className="mt-7 text-[clamp(2.2rem,5vw,3.6rem)] leading-[1.02]">
              Meta Ads, explained properly
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Explainers on the rules and mechanics that decide what Meta advertising for property
              costs: permits, housing rules, delivery, account structure and creative testing.
              Written to answer the question rather than to sell an engagement.
            </p>
          </div>
        </section>

        <section className="mx-auto max-w-4xl px-5 py-14 lg:px-8">
          <ul className="divide-y divide-border border-y border-border">
            {GUIDES.map((guide) => (
              <li key={guide.href}>
                <a href={guide.href} className="group block py-8 transition-colors">
                  <p className="text-[0.68rem] uppercase tracking-[0.18em] text-primary">
                    {guide.eyebrow} · {guide.minutes} min read
                  </p>
                  <h2 className="mt-3 text-2xl leading-tight transition-colors group-hover:text-primary sm:text-[1.7rem]">
                    {guide.title}
                  </h2>
                  <p className="mt-3 max-w-2xl leading-relaxed text-muted-foreground">
                    {guide.blurb}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-primary">
                    Read the guide <ArrowRight className="size-4" />
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}
